import { appConfigStore } from 'Models/AppConfigStore';
import { BookingEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import { bookingStatus, bookingTransactionStatus } from 'Models/Enums';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import type { CheckInBookingOverviewDto } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInEntities/CheckInBookingOverviewDto';

/**
 * What the modify and cancel rules need to know about a booking, separated from `BookingEntity` so the
 * check-in screen — which holds a `CheckInBookingOverviewDto` — is gated by exactly the same rules as the
 * booking detailed view. Build one with `bookingRuleFacts` or `checkInBookingRuleFacts`.
 */
export interface BookingRuleFacts {
	bookingStatus?: bookingStatus;
	transactionStatus?: bookingTransactionStatus;
	isGuest: boolean;
	legacyId?: string;
	/** ANY alteration invoiced. */
	hasInvoicedAlteration: boolean;
	/** Undefined only when a `BookingEntity` arrived without its booked summary. */
	departureDateTime?: Date;
	closedForBookingType?: boolean;
	checkInFinalised: boolean;
	checkedIn: boolean;
	checkInStatusId?: string;
}

export function bookingRuleFacts(booking: BookingEntity): BookingRuleFacts {
	// Missing summary is not thrown here: `canModifyBooking` lets an override user through before it
	// reads the trip, and throws only past that point.
	const ferryTrip = booking.bookedSummary?.ferryTrip;
	return {
		bookingStatus: booking.bookingStatus,
		transactionStatus: booking.transactionStatus,
		isGuest: !!booking.user?.isGuest,
		legacyId: booking.legacyId,
		hasInvoicedAlteration: booking.alterations.some(x => x.isInvoiced),
		departureDateTime: ferryTrip?.departureDateTime,
		closedForBookingType: ferryTrip?.isClosedForSpecificBookingType(booking.typeOfBooking()),
		checkInFinalised: ferryTrip?.checkInFinalised ?? false,
		checkedIn: !!booking.checkedIn,
		checkInStatusId: booking.checkInStatusId,
	};
}

/**
 * @param liveCheckInFinalised the trip's finalised flag as check-in holds it, which updates while the
 * screen is open; the booking's own copy is only as fresh as the last read.
 */
export function checkInBookingRuleFacts(
	booking: CheckInBookingOverviewDto,
	liveCheckInFinalised: boolean,
): BookingRuleFacts {
	// Optional like `bookingRuleFacts`: a check-in booking can arrive without its booked summary.
	const ferryTrip = booking.bookedSummary?.ferryTrip;
	return {
		bookingStatus: booking.bookingStatus,
		transactionStatus: booking.transactionStatus,
		isGuest: !!booking.user?.isGuest,
		legacyId: booking.legacyId,
		hasInvoicedAlteration: !!booking.hasInvoicedAlteration,
		departureDateTime: ferryTrip?.departureDateTime,
		closedForBookingType: ferryTrip?.isClosedForSpecificBookingType(booking.getBookingType()),
		checkInFinalised: liveCheckInFinalised || (ferryTrip?.checkInFinalised ?? false),
		checkedIn: !!booking.checkedIn,
		checkInStatusId: booking.checkInStatusId,
	};
}

export function canModifyBooking(booking: BookingEntity): boolean {
	return canModifyBookingFromFacts(bookingRuleFacts(booking));
}

function canModifyBookingFromFacts(facts: BookingRuleFacts): boolean {
	// When the finalised-check-in lock is on, a booking on a trip whose check-in has been
	// finalised can't be modified (edited or cancelled) — mirrors the backend guard, and applies
	// to everyone including staff (un-finalise the trip to make changes).
	if (appConfigStore.lockChangesWhenCheckInFinalised && facts.checkInFinalised) {
		return false;
	}

	if (!store.isStaff && facts.isGuest) {
		// Guests cannot edit their bookings
		return false;
	}

	// The server skips cutoffs, departure and closed-trip checks for this capability, and only for it.
	if (store.can('OverrideBookingRestrictions')) {
		return true;
	}

	// If the booking does not have `PAID` status or `AWAITING status and the user is not staff+
	if (
		facts.transactionStatus !== 'PAID' &&
		facts.transactionStatus !== 'AWAITING' &&
		facts.transactionStatus !== 'UNPAID' &&
		!store.isStaff
	) {
		return false;
	}

	// If any of the alterations associated to the booking have `INVOICED` transactions
	if (facts.hasInvoicedAlteration && !store.isStaff) {
		return false;
	}

	if (facts.departureDateTime === undefined) {
		throw new Error('booking.bookedSummary is undefined');
	}

	if (isBeforeNow(facts.departureDateTime)) {
		return false;
	}

	// Check if any of the booking types are closed (if this booking is of that type)
	if (facts.closedForBookingType) {
		return false;
	}

	if (facts.checkedIn || isNotNullOrUndefined(facts.checkInStatusId)) {
		return false;
	}

	return true;
}

export function canEditBooking(booking: BookingEntity): boolean {
	// Editing is booking: staff without BookOnBehalfOfCustomer are refused it by the server, which
	// showed up as "User mismatch" after they had walked through the wizard. Cancelling is separate.
	if (!store.mayMakeBookings) {
		return false;
	}

	if (!store.isStaff && booking.user?.isGuest) {
		// Guests cannot edit their bookings
		return false;
	}

	// No one can edit freight bookings
	if (booking.isFreightBooking()) {
		return false;
	}
	if (!appConfigStore.migratedBookingsModificationEnabled && !!booking.legacyId) {
		// No one can edit migrated bookings when setting is enabled
		return false;
	}

	// Currently no one can edit an unpaid booking until payment has been taken
	if (booking.transactionStatus === 'UNPAID') {
		return false;
	}

	// Can't edit driverless vehicle bookings that have a certain config set up because the pricing
	// is not working for alterations yet
	if (!booking.hasPassengers() && appConfigStore.DriverlessVehicleBookingPriceIncludesAdultPrice) {
		return false;
	}

	return canModifyBooking(booking);
}

export function canCancelBooking(booking: BookingEntity): boolean {
	return canCancelBookingFromFacts(bookingRuleFacts(booking));
}

function canCancelBookingFromFacts(facts: BookingRuleFacts): boolean {
	if (!store.isStaff && facts.isGuest) {
		// Guests cannot edit their bookings
		return false;
	}

	if (!appConfigStore.migratedBookingsModificationEnabled && !!facts.legacyId && !store.isStaff) {
		// Customer users cannot cancel migrated bookings
		return false;
	}
	return canModifyBookingFromFacts(facts);
}

/**
 * The state a booking must be in to be cancelled, on top of `canCancelBooking`'s permission rules —
 * booked, not departed (unless the user may override restrictions), and not checked in. Checked in
 * blocks everyone, staff included.
 */
export function bookingIsInCancellableState(facts: BookingRuleFacts): boolean {
	return (
		((facts.departureDateTime !== undefined && !isBeforeNow(facts.departureDateTime)) ||
			store.can('OverrideBookingRestrictions')) &&
		facts.bookingStatus === 'BOOKED' &&
		!facts.checkedIn &&
		isNullOrUndefined(facts.checkInStatusId)
	);
}

/**
 * Whether to offer Cancel on a booking — everything the booking detailed view checks before it shows
 * its cancel action. The server checks again before the wizard opens and when the cancel is submitted.
 */
export function canOfferCancellation(facts: BookingRuleFacts): boolean {
	// Called while rendering, so it must not reach `canModifyBookingFromFacts`'s throw on a missing
	// summary. Without a departure the booking is not cancellable anyway, unless the user may override.
	if (facts.departureDateTime === undefined && !store.can('OverrideBookingRestrictions')) {
		return false;
	}
	return canCancelBookingFromFacts(facts) && bookingIsInCancellableState(facts);
}
